import type { BookingState } from '../state/types';

/**
 * Sentry `guard` tag value for the genuine fake-confirmation signature: a user
 * reached /booking-final with an ACTIVE booking session but no committed
 * reservation. This is the 2026-05-25 incident signal and the canary metric
 * for the skip-confirm rollout — the `booking-final-no-reservation-id` Sentry
 * alert (docs/runbooks/sentry-alerts.md) keys off this exact value.
 */
export const BOOKING_FINAL_NO_RESERVATION_TAG = 'booking-final-no-reservation-id';

/**
 * Sentry `guard` tag value for the BENIGN case: a cold-load / deep-link /
 * bookmark / search-engine landing on /booking-final with no active session
 * at all. Deliberately DISTINCT from the alert tag so these unactionable
 * events form a separate, debug-level Sentry issue and never trip the alert
 * (Cluster A1/Sub-Y noise — see docs/plans/sentry-bf-systematic-fix.md).
 */
export const BOOKING_FINAL_NO_SESSION_TAG = 'booking-final-no-session';

export interface BookingFinalGuardClassification {
  message: string;
  level: 'warning' | 'debug';
  guardTag: typeof BOOKING_FINAL_NO_RESERVATION_TAG | typeof BOOKING_FINAL_NO_SESSION_TAG;
  guardSubset: 'actionable' | 'no_active_session';
}

/**
 * Is there ANY sign that the user actually progressed through the booking
 * flow? Standard bookings carry `selectedDate`/`selectedTime`; these are null
 * on a cold landing (deep-link / bookmark / search-engine hit) and populated
 * once the user picks a slot.
 *
 * But the non-standard flows reach /booking-final WITHOUT those fields:
 *   - waiting-list bookings use `wlDates` (+ `flowType === 'waiting_list'`),
 *     never selectedDate/selectedTime
 *   - early-access (`isEarlyAccessFlow`) and agent (`isAgentReservation`)
 *     bookings are flag-driven
 * A broken commit on ANY of these is exactly the 2026-05-25 fake-confirmation
 * P0, so they MUST stay actionable. We key on the durable flow flags (not just
 * `wlDates.length`) so the signal survives even if the date arrays were
 * cleared. All of these default to benign values in DEFAULT_BOOKING_STATE
 * ('standard' / false / []), so a true cold load still reads as session-less.
 *
 * Deliberately NOT `startedAt`: BookingProvider stamps `startedAt: Date.now()`
 * into DEFAULT_BOOKING_STATE on every mount, so it's set even on a pure cold
 * load and cannot discriminate. We err on the side of `warning` — any flow
 * progress at all keeps the actionable signal — so a possible real incident is
 * never silently downgraded.
 */
function hasActiveSession(state: BookingState): boolean {
  return Boolean(
    state.selectedDate ||
      state.selectedTime ||
      state.flowType === 'waiting_list' ||
      state.wlDates.length > 0 ||
      state.isEarlyAccessFlow ||
      state.isAgentReservation,
  );
}

/**
 * Classifies a "reached /booking-final without a reservationId" guard event so
 * the actionable fake-confirmation signal stays loud (warning + alert tag)
 * while benign cold-load noise is downgraded to a separate debug-level issue.
 *
 * Mirrors the already-shipped PaymentPage guard downgrade, but discriminates
 * on booking STATE rather than `document.referrer`: for BN the downgrade would
 * suppress the very alert we rely on, so referrer (which can be empty for a
 * real direct-then-book user) is too risky here.
 */
export function classifyBookingFinalGuard(
  state: BookingState,
): BookingFinalGuardClassification {
  if (hasActiveSession(state)) {
    return {
      message: 'BookingFinalPage rendered without reservationId',
      level: 'warning',
      guardTag: BOOKING_FINAL_NO_RESERVATION_TAG,
      guardSubset: 'actionable',
    };
  }

  return {
    message: 'BookingFinalPage rendered without reservationId (no active session)',
    level: 'debug',
    guardTag: BOOKING_FINAL_NO_SESSION_TAG,
    guardSubset: 'no_active_session',
  };
}
